import { c as _c } from "react/compiler-runtime";
import type { ApiNodeData, GraphOperation, GraphOperationResult, OnboardingDataEntry } from '@onefootprint/request-types/dashboard';
import { Divider, useEscOperationStack } from '@onefootprint/ui';
import { Position } from '@xyflow/react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import useSession from '@/hooks/use-session';
import useCollapsedNodes from '../../hooks/use-collapsed-nodes';
import useDirty from '../../hooks/use-dirty';
import useEdit from '../../hooks/use-edit';
import useNodeContext from '../../hooks/use-node-context';
import useSidebar from '../../hooks/use-sidebar';
import useStore from '../../hooks/use-store';
import type { NodeData } from '../../playbook-builder.types';
import CustomHandle from '../custom-handle';
import type { Banner } from '../error-warning-list/error-warning-list';
import ErrorsWarningsDisplay, { type CustomErrorComponent } from '../errors-warnings-display';
import { useNodePanel } from '../node-panel/node-panel-context';
import OnboardingDataSection from '../onboarding-data-section';
import AddAfterNode from './components/add-after-node';
import AddBeforeNode from './components/add-before-node';
import { BaseNodeButton } from './components/base-node-button';
import CollapseToggle from './components/collapse-toggle';
import NodeIcon from './components/node-icon';
import { RunNodeTest } from './components/run-node-test';
import { useGetNodeInfo } from './hooks/use-get-node-info';
export type ApplyEditMutation = {
  mutateAsync: (operation: GraphOperation) => Promise<GraphOperationResult | undefined>;
  isPending: boolean;
};
type SidebarProps = {
  mutation: ApplyEditMutation;
  canEdit: boolean;
  isFirmEmployee: boolean;
};
type BaseNodeProps = {
  nodeId: string;
  data: NodeData<ApiNodeData>;
  sidebar?: {
    render: (props: SidebarProps) => React.ReactNode;
    customErrorComponents?: Record<string, CustomErrorComponent>;
    /** Banners the form shows next to their field, which the panel's banner list leaves out. */
    isBannerInline?: (banner: Banner) => boolean;
    /** The `id` of the form that is being rendered in the drawer. This allows the save button to submit the form. */
    form?: string;
    size?: 'md' | 'lg' | 'xl';
    /** Replaces the generic runtime-assignment list with a node-specific view of its trace data. */
    renderTraceData?: (onboardingData: OnboardingDataEntry[]) => React.ReactNode;
  };
  /** When true, renders a collapse/expand triangle for branch/conditional nodes */
  collapsible?: boolean;
  /** Custom click handler. If provided, overrides the default sidebar open behavior. */
  onClick?: () => void;
};
const BaseNode = t0 => {
  const $ = _c(80);
  const {
    nodeId,
    data,
    sidebar,
    collapsible,
    onClick
  } = t0;
  const {
    render: renderSidebar,
    customErrorComponents,
    renderTraceData,
    isBannerInline
  } = sidebar ?? {};
  const {
    data: t1
  } = useStore();
  const {
    canEdit,
    traceEntries: t2,
    graph,
    pristineNodeIds
  } = t1;
  const traceEntries = t2 === undefined ? [] : t2;
  const {
    isCollapsed,
    toggleCollapsed
  } = useCollapsedNodes();
  const {
    data: t3
  } = useSession();
  const {
    user
  } = t3;
  const {
    open: openNode,
    close: closeNode,
    isOpen: isDrawerOpen
  } = useSidebar();
  const {
    setDescriptor,
    bodySlot
  } = useNodePanel();
  let t4;
  if ($[0] !== isDrawerOpen || $[1] !== nodeId) {
    t4 = isDrawerOpen(nodeId);
    $[0] = isDrawerOpen;
    $[1] = nodeId;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const isOpen = t4;
  const getNodeInfo = useGetNodeInfo(graph);
  const {
    isAnyComponentDirty,
    isAnyComponentBlocking,
    clearDirtyComponents
  } = useDirty();
  const {
    nodeErrors,
    node
  } = useNodeContext(nodeId);
  let t5;
  if ($[3] !== data || $[4] !== getNodeInfo) {
    t5 = getNodeInfo(data);
    $[3] = data;
    $[4] = getNodeInfo;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    title,
    description,
    icon,
    subtitle
  } = t5;
  const {
    onEscKeyDown
  } = useEscOperationStack();
  const onboardingData = traceEntries.find(entry => entry.decisionNodeId === nodeId)?.onboardingData || [];
  const editMutation = useEdit();
  let t6;
  if ($[6] !== clearDirtyComponents || $[7] !== closeNode) {
    t6 = () => {
      clearDirtyComponents();
      closeNode();
    };
    $[6] = clearDirtyComponents;
    $[7] = closeNode;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleClose = t6;
  let t7;
  if ($[9] !== isAnyComponentDirty || $[10] !== isOpen || $[11] !== nodeId || $[12] !== onClick || $[13] !== onEscKeyDown || $[14] !== openNode) {
    t7 = () => {
      if (onClick) {
        return onClick();
      }
      if (isOpen) {
        return onEscKeyDown();
      }
      if (!isOpen && isAnyComponentDirty) {
        return onEscKeyDown();
      }
      return openNode(nodeId);
    };
    $[9] = isAnyComponentDirty;
    $[10] = isOpen;
    $[11] = nodeId;
    $[12] = onClick;
    $[13] = onEscKeyDown;
    $[14] = openNode;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleNodeClick = t7;
  let t8;
  if ($[16] !== editMutation.isPending || $[17] !== editMutation.mutateAsync) {
    t8 = {
      mutateAsync: editMutation.mutateAsync,
      isPending: editMutation.isPending
    };
    $[16] = editMutation.isPending;
    $[17] = editMutation.mutateAsync;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const mutation = t8;
  const isActive = !!sidebar && isOpen;
  let t9;
  if ($[19] !== nodeId || $[20] !== pristineNodeIds) {
    t9 = pristineNodeIds.includes(nodeId);
    $[19] = nodeId;
    $[20] = pristineNodeIds;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const isNew = t9;
  let t10;
  if ($[22] !== canEdit || $[23] !== clearDirtyComponents || $[24] !== handleClose || $[25] !== isActive || $[26] !== isAnyComponentBlocking || $[27] !== isAnyComponentDirty || $[28] !== isNew || $[29] !== nodeId || $[30] !== setDescriptor || $[31] !== sidebar?.form || $[32] !== sidebar?.size) {
    t10 = () => {
      if (!isActive) {
        return;
      }
      setDescriptor({
        nodeId,
        form: sidebar?.form,
        size: sidebar?.size,
        canEdit,
        isDirty: isAnyComponentDirty,
        isSaveBlocked: isAnyComponentBlocking,
        isNew,
        clearDirty: clearDirtyComponents,
        onClose: handleClose
      });
      return () => setDescriptor(current => current?.nodeId === nodeId ? null : current);
    };
    $[22] = canEdit;
    $[23] = clearDirtyComponents;
    $[24] = handleClose;
    $[25] = isActive;
    $[26] = isAnyComponentBlocking;
    $[27] = isAnyComponentDirty;
    $[28] = isNew;
    $[29] = nodeId;
    $[30] = setDescriptor;
    $[31] = sidebar?.form;
    $[32] = sidebar?.size;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const t11 = sidebar?.form;
  const t12 = sidebar?.size;
  let t13;
  if ($[34] !== canEdit || $[35] !== isActive || $[36] !== isAnyComponentBlocking || $[37] !== isAnyComponentDirty || $[38] !== isNew || $[39] !== nodeId || $[40] !== t11 || $[41] !== t12) {
    t13 = [isActive, nodeId, t11, t12, canEdit, isAnyComponentDirty, isAnyComponentBlocking, isNew];
    $[34] = canEdit;
    $[35] = isActive;
    $[36] = isAnyComponentBlocking;
    $[37] = isAnyComponentDirty;
    $[38] = isNew;
    $[39] = nodeId;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  useEffect(t10, t13);
  const panelBody = isActive && bodySlot ? createPortal(<><div className="flex gap-3 items-start pb-4 border-b border-tertiary p-4 z-10 sticky top-0 bg-primary"><NodeIcon {...icon} /><div className="flex grow flex-col gap-0.5"><h2 className="text-label-2 text-primary">{title.uppercase}</h2><div className="text-body-2 text-tertiary">{description}</div></div>{node && <RunNodeTest node={node} disabled={isAnyComponentDirty} />}</div><ErrorsWarningsDisplay errors={nodeErrors.filter(banner => !isBannerInline?.(banner))} mutation={mutation} customErrorComponents={customErrorComponents} /><div key={data.graphId} className="p-4 flex flex-col gap-4">{renderSidebar?.({
        mutation,
        canEdit,
        isFirmEmployee: !!user?.isFirmEmployee
      })}</div>{onboardingData.length > 0 && <div className="pt-4"><Divider />{renderTraceData ? <div className="p-4">{renderTraceData(onboardingData)}</div> : <OnboardingDataSection onboardingData={onboardingData} />}</div>}</>, bodySlot) : null;
  let t14;
  if ($[43] !== nodeId) {
    t14 = <CustomHandle type="target" position={Position.Top} id={nodeId} />;
    $[43] = nodeId;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== data.subject || $[46] !== mutation || $[47] !== nodeId) {
    t15 = <AddBeforeNode nodeId={nodeId} mutation={mutation} subject={data.subject} />;
    $[45] = data.subject;
    $[46] = mutation;
    $[47] = nodeId;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== collapsible || $[50] !== isCollapsed || $[51] !== nodeId || $[52] !== toggleCollapsed) {
    t16 = collapsible && <CollapseToggle isCollapsed={isCollapsed(nodeId)} onToggle={() => toggleCollapsed(nodeId)} />;
    $[49] = collapsible;
    $[50] = isCollapsed;
    $[51] = nodeId;
    $[52] = toggleCollapsed;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== data || $[55] !== handleNodeClick || $[56] !== icon || $[57] !== isOpen || $[58] !== nodeId || $[59] !== subtitle || $[60] !== title.uppercase) {
    t17 = <BaseNodeButton id={nodeId} label={title.uppercase} data={data} icon={icon} subtitle={subtitle} onClick={handleNodeClick} isActive={isOpen} />;
    $[54] = data;
    $[55] = handleNodeClick;
    $[56] = icon;
    $[57] = isOpen;
    $[58] = nodeId;
    $[59] = subtitle;
    $[60] = title.uppercase;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== t16 || $[63] !== t17) {
    t18 = <div className="relative">{t16}{t17}</div>;
    $[62] = t16;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== data.subject || $[66] !== mutation || $[67] !== nodeId) {
    t19 = <AddAfterNode nodeId={nodeId} mutation={mutation} subject={data.subject} />;
    $[65] = data.subject;
    $[66] = mutation;
    $[67] = nodeId;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  let t20;
  if ($[69] !== nodeId) {
    t20 = <CustomHandle type="source" position={Position.Bottom} id={nodeId} />;
    $[69] = nodeId;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] !== nodeId || $[72] !== panelBody || $[73] !== t14 || $[74] !== t15 || $[75] !== t18 || $[76] !== t19 || $[77] !== t20 || $[78] !== title.uppercase) {
    t21 = <div className="relative" data-percy-context-id={nodeId} data-percy-context-label={title.uppercase}>{t14}{t15}{t18}{t19}{t20}{panelBody}</div>;
    $[71] = nodeId;
    $[72] = panelBody;
    $[73] = t14;
    $[74] = t15;
    $[75] = t18;
    $[76] = t19;
    $[77] = t20;
    $[78] = title.uppercase;
    $[79] = t21;
  } else {
    t21 = $[79];
  }
  return t21;
};
export default BaseNode;